import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import React, { Fragment, useContext, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { PagedDataView } from "../lists/PagedDataView/PagedDataView";
import { IUsersGet, User, api } from "../../logic/api";
import UserDetailDialog from "./UserDetailDialog";
import UserListItem from "./UserListItem";
import SearchIcon from "@mui/icons-material/Search";
import SwitchField from "../editable/EditableField/fields/SwitchField";
import flex from "../../styles/flex";
import { useRoles } from "./rolesAndPermissions/useRoles";
import { isAdmin } from "../../utility/utils";
import { SessionContext } from "../../utility/useSession";
import { useTranslation } from "react-i18next";

const UserManagement = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreateUser, setShowCreateUser] = useState<boolean>(false);
  const [userListFetchParams, setUserListFetchParams] = useState<IUsersGet>({});
  const [filter, setFilter] = useState("");
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { data: rolesData } = useRoles();
  const availableRoles = rolesData?.roles || [];

  const UserDialog = () => {
    return (
      <UserDetailDialog
        user={selectedUser}
        show={showCreateUser}
        availableRoles={availableRoles}
        onClose={(didSave) => {
          setSelectedUser(null);
          setShowCreateUser(false);
          if (didSave == true) {
            setUserListFetchParams({ ...userListFetchParams });
          }
        }}
      ></UserDetailDialog>
    );
  };

  return (
    <div>
      <UserDialog />
      <div style={{ padding: 10, textAlign: "right" }}>
        <div style={flex.flexRows}>
          <div style={flex.flexAuto}>
            <TextField
              autoFocus={false}
              label={t("users.searchUsers")}
              variant="outlined"
              size="small"
              type="search"
              value={filter}
              onChange={(e) => {
                setFilter(e.currentTarget.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.currentTarget) {
                    e.currentTarget.blur();
                    setUserListFetchParams({
                      ...userListFetchParams,
                      filter: filter,
                    });
                  }
                  return false;
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position={"end"}>
                    <IconButton
                      style={{ padding: 0 }}
                      onClick={() => {
                        setUserListFetchParams({
                          ...userListFetchParams,
                          filter: filter,
                        });
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          {isAdmin(session) && (
            <Fragment>
              <div style={flex.flexAuto}>
                <SwitchField
                  onChange={(includeInactive) => {
                    setUserListFetchParams({
                      ...userListFetchParams,
                      includeInactive: includeInactive,
                    });
                  }}
                  value={userListFetchParams.includeInactive}
                ></SwitchField>
              </div>
              <div style={flex.flexAuto}>
                <div
                  style={{ textAlign: "right", paddingTop: 10, color: "grey" }}
                >
                  {t("mobile.includeDisabled")}
                </div>
              </div>
            </Fragment>
          )}
          <div style={flex.flexFill}></div>
          {isAdmin(session) && (
            <div style={flex.flexAuto}>
              <Button
                style={{ marginLeft: 10 }}
                variant="contained"
                color="secondary"
                onClick={() => {
                  setShowCreateUser(true);
                }}
              >
                <FiPlus style={{ marginRight: 10 }}></FiPlus>{t("users.newUser")}</Button>
            </div>
          )}
        </div>
      </div>
      <PagedDataView<IUsersGet, User>
        apiHook={api.hooks.users.list}
        parameters={userListFetchParams}
        renderItem={(item) => {
          return (
            <UserListItem
              user={item}
              onEditUser={(user) => {
                setSelectedUser(user);
                setShowCreateUser(true);
              }}
            ></UserListItem>
          );
        }}
      >
        {t("users.noUsers")}
      </PagedDataView>
    </div>
  );
};

export default UserManagement;
